import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "react-use";

import { makeRequest } from "../../utils/request";
import { PageLayout } from "../../components/PageLayout";
import { AdminDetailDrawer, AdminTable, Field, Section, useAdminFetch } from "../../components/admin";
import type { AdminColumn } from "../../components/admin";
import { Alert, AlertDescription, Badge, Button, Dialog, InputField, SwitchField, Text, Tooltip, View } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { IconButton } from "../../components/base/IconButton";
import { getColorForName } from "../../utils/colors";
import { formatDateShort } from "../../utils/adminFormat";
import { User } from "../../redux/reducers/userSlice";

const PAGE_SIZE = 50;

const getInitials = (user: User) => {
  const source = user.name || user.email;
  const parts = source.trim().split(/\s+/);
  return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : source.slice(0, 2)).toUpperCase();
};

export const AdminUsersPage = () => {
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");
  useDebounce(() => setQuery(searchText.trim()), 300, [searchText]);

  // Searching swaps the paged list for the (unpaged) search results; clearing restores it.
  const listUrl = query ? `/v1/users/search?q=${encodeURIComponent(query)}` : `/v1/users?page=${page}&limit=${PAGE_SIZE}`;
  const { data: listData, isLoading, error, refresh } = useAdminFetch<{ data: User[]; metadata?: { page: number; limit: number; total: number } }>(listUrl);
  const { data: orgsData } = useAdminFetch<{ data: { id: string; name: string }[] }>("/v1/organizations?limit=5000");

  const users = useMemo(() => listData?.data ?? [], [listData]);
  const orgNames = useMemo(() => new Map((orgsData?.data ?? []).map((org) => [org.id, org.name])), [orgsData]);

  const [selected, setSelected] = useState<User | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  useEffect(() => {
    setEditName(selected?.name ?? "");
    setEditEmail(selected?.email ?? "");
    setEditIsAdmin(selected?.isAdmin ?? false);
    setMutationError(null);
  }, [selected]);

  const hasChanges = !!selected && (editName !== (selected.name ?? "") || editEmail !== selected.email || editIsAdmin !== selected.isAdmin);

  const onSave = async () => {
    if (!selected || !hasChanges) return;
    const body: Record<string, unknown> = {};
    if (editName !== (selected.name ?? "")) body.name = editName;
    if (editEmail !== selected.email) body.email = editEmail;
    if (editIsAdmin !== selected.isAdmin) body.isAdmin = editIsAdmin;
    setIsSaving(true);
    setMutationError(null);
    try {
      const { data } = await makeRequest<{ data: User }>(`/v1/users/${selected.id}`, { method: "PATCH", body });
      setSelected(data);
      refresh();
    } catch (e) {
      setMutationError(e instanceof Error ? e.message : "Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const onDelete = async () => {
    if (!selected) return;
    setIsDeleting(true);
    setMutationError(null);
    try {
      await makeRequest(`/v1/users/${selected.id}`, { method: "DELETE" });
      setIsConfirmingDelete(false);
      setSelected(null);
      refresh();
    } catch (e) {
      setMutationError(e instanceof Error ? e.message : "Failed to delete user");
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = useMemo<AdminColumn<User>[]>(
    () => [
      {
        key: "name",
        header: "Name",
        width: "1.3fr",
        render: (row) => (
          <View className="flex min-w-0 flex-row items-center gap-2.5">
            <View className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${getColorForName(row.name || row.id)}`}>
              <Text className="text-xs font-medium text-white">{getInitials(row)}</Text>
            </View>
            {row.name ? (
              <Text className="truncate text-sm font-medium text-gray-900 dark:text-white">{row.name}</Text>
            ) : (
              <Text className="text-sm text-gray-400 dark:text-gray-500">—</Text>
            )}
          </View>
        ),
      },
      {
        key: "email",
        header: "Email",
        width: "1.5fr",
        render: (row) => (
          <View className="flex min-w-0 flex-row items-center gap-1.5">
            <Text className="truncate text-sm">{row.email}</Text>
            {row.emailVerified && (
              <Tooltip content="Verified">
                <Icon
                  name="check-circle"
                  size={14}
                  className="shrink-0 text-emerald-500"
                />
              </Tooltip>
            )}
          </View>
        ),
      },
      {
        key: "defaultOrganizationId",
        header: "Default org",
        width: "1fr",
        render: (row) => {
          if (!row.defaultOrganizationId) return <Text className="text-sm text-gray-400 dark:text-gray-500">—</Text>;
          const orgName = orgNames.get(row.defaultOrganizationId);
          return orgName ? (
            <Text className="block truncate text-sm">{orgName}</Text>
          ) : (
            <Text className="block truncate font-mono text-xs text-gray-400 dark:text-gray-500">{row.defaultOrganizationId}</Text>
          );
        },
      },
      {
        key: "createdAt",
        header: "Signed up",
        width: "130px",
        align: "right",
        render: (row) => <Text className="text-sm text-gray-500 dark:text-gray-400">{formatDateShort(row.createdAt)}</Text>,
        sortValue: (row) => row.createdAt,
      },
      {
        key: "isAdmin",
        header: "Admin",
        width: "90px",
        render: (row) =>
          row.isAdmin ? (
            <Badge className="!bg-purple-50 !text-purple-600 dark:!bg-purple-900/30 dark:!text-purple-400">Admin</Badge>
          ) : (
            <Text className="text-sm text-gray-400 dark:text-gray-500">—</Text>
          ),
      },
    ],
    [orgNames],
  );

  const selectedOrgName = selected?.defaultOrganizationId ? orgNames.get(selected.defaultOrganizationId) : null;

  return (
    <PageLayout
      title="Users"
      description="All user accounts across the platform."
      headerAction={
        <IconButton
          icon="refresh"
          variant="info"
          onClick={refresh}
          title="Refresh"
        />
      }
    >
      <View className="mt-6 flex flex-col gap-4">
        <InputField
          value={searchText}
          onChange={setSearchText}
          placeholder="Search by name or email…"
          className="max-w-sm"
        />

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <AdminTable
          columns={columns}
          rows={users}
          rowKey={(row) => row.id}
          onRowClick={(row) => setSelected(row)}
          selectedKey={selected?.id ?? null}
          isLoading={isLoading}
          emptyState={{
            icon: "users",
            title: query ? "No users match your search" : "No users yet",
            description: query ? "Try a different name or email." : undefined,
          }}
          defaultSort={{ key: "createdAt", dir: "desc" }}
          pagination={query ? undefined : { page, pageSize: PAGE_SIZE, total: listData?.metadata?.total ?? 0, onPageChange: setPage }}
        />
      </View>

      <AdminDetailDrawer
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name || selected?.email || ""}
        subtitle={selected?.name ? selected.email : undefined}
      >
        {selected && (
          <>
            <Section title="Profile">
              <View className="flex flex-col gap-2">
                <Field
                  label="ID"
                  mono
                >
                  {selected.id}
                </Field>
                <Field label="Default org">{selectedOrgName || selected.defaultOrganizationId || "—"}</Field>
                <Field label="Signed up">{formatDateShort(selected.createdAt)}</Field>
                <Field label="Email verified">{selected.emailVerified ? "Yes" : "No"}</Field>
              </View>
              <View className="mt-2 flex flex-col gap-3">
                <InputField
                  label="Name"
                  value={editName}
                  onChange={setEditName}
                />
                <InputField
                  label="Email"
                  value={editEmail}
                  onChange={setEditEmail}
                />
              </View>
            </Section>

            <Section title="Access">
              <SwitchField
                label="Platform admin"
                labelClassName="!w-full"
                className="!w-full"
                value={editIsAdmin ? "true" : "false"}
                onChange={(value: string | null) => setEditIsAdmin(value === "true")}
                options={[
                  { label: "Disabled", value: "false" },
                  { label: "Enabled", value: "true" },
                ]}
              />
            </Section>

            {mutationError && (
              <Alert variant="destructive">
                <AlertDescription>{mutationError}</AlertDescription>
              </Alert>
            )}

            <View className="flex flex-row items-center justify-between gap-3 border-t border-gray-100 dark:border-gray-800 pt-4">
              <Button
                variant="danger"
                size="sm"
                outline
                onClick={() => setIsConfirmingDelete(true)}
              >
                <Icon
                  name="trash"
                  size={14}
                />
                Delete user
              </Button>
              <Button
                variant="brand"
                size="sm"
                onClick={onSave}
                isDisabled={!hasChanges || isSaving}
              >
                {isSaving ? (
                  <Icon
                    name="loader"
                    size={14}
                    className="animate-spin"
                  />
                ) : (
                  <Icon
                    name="check"
                    size={14}
                  />
                )}
                Save changes
              </Button>
            </View>
          </>
        )}
      </AdminDetailDrawer>

      <Dialog
        isOpen={isConfirmingDelete}
        onClose={() => setIsConfirmingDelete(false)}
        title="Delete user"
        size="sm"
        actions={
          <>
            <Button
              variant="neutral"
              size="sm"
              outline
              onClick={() => setIsConfirmingDelete(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              outline
              onClick={onDelete}
              isDisabled={isDeleting}
            >
              {isDeleting ? (
                <Icon
                  name="loader"
                  size={14}
                  className="animate-spin"
                />
              ) : (
                <Icon
                  name="trash"
                  size={14}
                />
              )}
              Delete
            </Button>
          </>
        }
      >
        <View className="p-4">
          <Text className="text-sm text-gray-600 dark:text-gray-300">Delete {selected?.email}? This cannot be undone.</Text>
        </View>
      </Dialog>
    </PageLayout>
  );
};
